export interface ReviewSectionConfig {
  title: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  description: string;
  heroGradient: string;
  subcategories: string[];
  /** When true, the "Best-Of Guides" (top lists) section is hidden on this page. */
  hideTopLists?: boolean;
}

export const reviewSections: Record<string, ReviewSectionConfig> = {
  "power-tools": {
    title: "Power Tools",
    slug: "power-tools",
    metaTitle: "Best Power Tools Reviews UK 2026 | Expert Tested & Rated",
    metaDescription: "Expert UK power tool reviews including generators, pressure washers, cement mixers and more. Independent testing, honest ratings and buying advice.",
    description: "Expert reviews and buying guides for power tools, from generators and pressure washers to cement mixers. All independently tested in the UK.",
    heroGradient: "bg-linear-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20",
    subcategories: ["power-tools", "generators", "pressure-washers", "cement-mixers", "air-compressors", "compactor-plates", "air-fans-ventilators", "impact-drivers", "water-pumps", "mini-dumpers", "earth-augers", "welders", "cordless-drills", "impact-wrenches", "battery-ratchets", "mains-power-drills", "mains-sds-drills", "cordless-sds-drills", "cordless-screwdrivers", "magnetic-drills", "mains-screwdrivers", "twin-packs-kits", "right-angle-drills", "demolition-hammers", "drill-stands", "oscillating-multi-tools", "multi-tool-blades", "circular-saws", "circular-saw-blades", "circular-saw-bushing-washers", "cut-off-saws", "reciprocating-saws", "jigsaws", "routers-laminate-trimmers", "router-jigs-templates", "router-bit-sets", "router-bits", "router-guide-bushes", "router-accessories", "router-tables", "angle-grinders", "angle-grinder-accessories", "angle-grinder-discs", "power-planers", "electric-planer-blades", "planer-thicknessers", "orbital-sanders", "drywall-sanders", "polishers", "belt-sanders", "plunge-saws", "saw-guide-rails", "mini-rotary-tools", "rotary-tool-accessories", "nail-staple-guns", "biscuit-dowel-jointers", "heat-guns", "table-saws", "glue-guns", "power-tool-batteries", "autofeed-screwdrivers", "compound-mitre-saws", "concrete-grinders", "electric-die-grinders", "metal-nibblers-shears", "wet-dry-extractors", "powered-cutters", "site-radios", "wall-chasers", "bevellers", "powered-caulking-guns", "rebar-tying-tools", "riveters", "tile-cutters"],
  },
  "garden-machinery": {
    title: "Garden Machinery",
    slug: "garden-machinery",
    metaTitle: "Best Garden Machinery Reviews UK 2026 | Expert Tested",
    metaDescription: "In-depth UK reviews of garden machinery including lawn mowers, chainsaws, leaf blowers, hedge trimmers, strimmers, scarifiers and more.",
    description: "Comprehensive reviews of garden machinery from lawn mowers and chainsaws to leaf blowers, strimmers, and scarifiers. All tested in real UK gardens.",
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
    subcategories: ["lawn-mowers", "chainsaws", "leaf-blowers", "hedge-trimmers", "strimmers", "garden-multi-tools", "tillers-cultivators", "scarifiers", "shredders", "log-splitters", "trenchers", "stump-grinders", "carts-trailers"],
  },
  "hand-tools": {
    title: "Hand Tools",
    slug: "hand-tools",
    metaTitle: "Best Garden Hand Tools Reviews UK 2026 | Expert Picks",
    metaDescription: "Expert reviews of the best garden hand tools in the UK. Secateurs, spades, forks, trowels and loppers tested and rated by gardening experts.",
    description: "Honest reviews of garden hand tools including secateurs, spades, forks, trowels, and loppers. Tested by experienced gardeners across the UK.",
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50 dark:from-stone-950/20 dark:to-amber-950/20",
    subcategories: ["secateurs", "spades-forks", "trowels", "loppers", "hand-tools"],
  },
  "garden-kit": {
    title: "Garden Kit",
    slug: "garden-kit",
    metaTitle: "Best Garden Kit & Equipment Reviews UK 2026 | Tested",
    metaDescription: "Reviews of essential garden kit including compost bins, greenhouses, garden hoses, water butts and wheelbarrows. Independently tested in UK gardens.",
    description: "Reviews of essential garden kit and equipment from compost bins and greenhouses to hoses, water butts and wheelbarrows. All put through their paces in the UK.",
    heroGradient: "bg-linear-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20",
    subcategories: ["compost-bins", "compost", "greenhouses", "hoses", "hoses-reels", "sprinklers-irrigation", "watering-cans", "water-butts", "wheelbarrows", "planters-pots", "garden-hand-tools", "garden-kit"],
  },
  "outdoor-furniture": {
    title: "Outdoor Furniture",
    slug: "outdoor-furniture",
    metaTitle: "Best Outdoor Furniture Reviews UK 2026 | Expert Tested",
    metaDescription: "Expert reviews of outdoor furniture including garden chairs, rattan sets, patio furniture, fire pits, hot tubs and BBQs for UK gardens.",
    description: "In-depth reviews of outdoor furniture and garden living essentials, from rattan furniture sets and fire pits to hot tubs and BBQ pizza ovens.",
    heroGradient: "bg-linear-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 dark:to-blue-950/20",
    subcategories: ["garden-chairs", "rattan-furniture", "patio-sets", "garden-parasols", "garden-swings-hammocks", "rugs-outdoor", "fire-pits", "hot-tubs", "bbq-pizza-ovens", "wood-burning-stoves", "outdoor-furniture"],
  },
  "heating-cooling": {
    title: "Heating & Cooling",
    slug: "heating-cooling",
    metaTitle: "Best Garden & Workshop Heating & Cooling Reviews UK 2026",
    metaDescription: "Expert UK reviews of patio heaters, space heaters, wood burners, fire pits, air conditioners and workshop fans. Tested for real UK conditions.",
    description: "Reviews of heating and cooling kit for gardens, patios and workshops — from patio heaters and log burners to portable air conditioners and drum fans.",
    heroGradient: "bg-linear-to-br from-orange-50 to-rose-50 dark:from-orange-950/20 dark:to-rose-950/20",
    subcategories: ["patio-heaters", "space-heaters", "wood-burning-stoves", "fire-pits", "air-fans-ventilators", "air-conditioning-coolers"],
  },
  "garden-living": {
    title: "Garden Living",
    slug: "garden-living",
    metaTitle: "Best Gazebos, Parasols, Sheds & Garden Storage Reviews UK 2026",
    metaDescription: "In-depth UK reviews of gazebos, garden parasols, sheds, storage boxes, hammocks and garden decor for making the most of your outdoor space.",
    description: "Reviews and buying guides for garden living essentials — gazebos, parasols, sheds, storage, hammocks and decor to transform your outdoor space.",
    heroGradient: "bg-linear-to-br from-lime-50 to-emerald-50 dark:from-lime-950/20 dark:to-emerald-950/20",
    subcategories: ["gazebos-marquees", "garden-parasols", "garden-storage", "garden-sheds", "garden-swings-hammocks", "garden-decor", "rugs-outdoor"],
  },
  "bbq-outdoor-cooking": {
    title: "BBQ & Outdoor Cooking",
    slug: "bbq-outdoor-cooking",
    metaTitle: "Best BBQs, Pizza Ovens & Outdoor Cooking Reviews UK 2026",
    metaDescription: "Independent UK reviews of BBQs, smokers, pizza ovens and outdoor kitchens. Charcoal, gas and kamado grills tested and rated.",
    description: "Expert reviews of BBQs, smokers, pizza ovens and outdoor kitchens — charcoal, gas and kamado grills put to the test in UK gardens.",
    heroGradient: "bg-linear-to-br from-amber-50 to-red-50 dark:from-amber-950/20 dark:to-red-950/20",
    subcategories: ["bbqs-outdoor-cooking"],
  },
  "watering-irrigation": {
    title: "Watering & Irrigation",
    slug: "watering-irrigation",
    metaTitle: "Best Hoses, Sprinklers & Irrigation Reviews UK 2026",
    metaDescription: "Expert UK reviews of garden hoses, hose reels, sprinklers, irrigation kits, watering cans, water butts and planters. Independently tested.",
    description: "Reviews of watering and irrigation kit — from hose reels and sprinkler systems to watering cans, water butts and planters.",
    heroGradient: "bg-linear-to-br from-sky-50 to-cyan-50 dark:from-sky-950/20 dark:to-cyan-950/20",
    subcategories: ["hoses-reels", "sprinklers-irrigation", "watering-cans", "water-butts", "water-pumps", "planters-pots"],
  },
  "plants-growing": {
    title: "Plants & Growing",
    slug: "plants-growing",
    metaTitle: "Best Plants, Seeds & Growing Supplies Reviews UK 2026",
    metaDescription: "UK reviews and buying guides for plants, flowers, vegetables, fruit trees, seeds, compost and greenhouses. Grow the best garden this year.",
    description: "Buying guides for plants and growing supplies — flowers, vegetables, fruit trees, seeds, compost and greenhouses for UK gardens.",
    heroGradient: "bg-linear-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20",
    subcategories: ["plants", "flowers", "vegetables", "fruits", "seeds", "compost", "greenhouses"],
    hideTopLists: true,
  },
  "pets-wildlife": {
    title: "Pets & Wildlife",
    slug: "pets-wildlife",
    metaTitle: "Best Pet & Wildlife Garden Products Reviews UK 2026",
    metaDescription: "Reviews of pet supplies and wildlife-friendly garden products — dog crates, cat trees, bird feeders, bird boxes and bird baths for UK gardens.",
    description: "Reviews of pet supplies and wildlife garden products, from dog kennels and cat trees to bird feeders, boxes and baths.",
    heroGradient: "bg-linear-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20",
    subcategories: ["pet-supplies", "bird-care"],
  },
  "play-leisure": {
    title: "Play & Leisure",
    slug: "play-leisure",
    metaTitle: "Best Trampolines, Hot Tubs & Kids Outdoor Play Reviews UK 2026",
    metaDescription: "Expert UK reviews of trampolines, hot tubs, swimming pools, playhouses, climbing frames and outdoor kids play equipment.",
    description: "Reviews of trampolines, hot tubs, pools and kids outdoor play equipment — everything for family fun in the garden.",
    heroGradient: "bg-linear-to-br from-pink-50 to-purple-50 dark:from-pink-950/20 dark:to-purple-950/20",
    subcategories: ["trampolines", "hot-tubs-pools", "kids-outdoor-play"],
  },
  "workshop-safety": {
    title: "Workshop & Safety",
    slug: "workshop-safety",
    metaTitle: "Best Workbenches, Ladders, Tool Chests & Safety Gear Reviews UK 2026",
    metaDescription: "Independent UK reviews of workbenches, vices, ladders, tool chests, automotive tools, safety boots, gloves and workshop PPE.",
    description: "Reviews of workshop and safety essentials — workbenches, ladders, tool storage, automotive tools, PPE and security kit.",
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50 dark:from-slate-950/20 dark:to-zinc-950/20",
    subcategories: ["workbenches-vices", "ladders-steps", "tool-chests", "automotive-tools", "safety-workwear", "locks-security", "winches-lifting", "cleaning-supplies"],
  },
  "landscaping": {
    title: "Landscaping",
    slug: "landscaping",
    metaTitle: "Best Fencing, Decking & Paving Reviews UK 2026",
    metaDescription: "UK reviews and buying guides for fencing, gates, decking, paving slabs and hard landscaping materials.",
    description: "Reviews of hard landscaping essentials — fence panels, gates, decking boards and paving slabs for UK gardens.",
    heroGradient: "bg-linear-to-br from-stone-50 to-neutral-50 dark:from-stone-950/20 dark:to-neutral-950/20",
    subcategories: ["fencing-gates", "decking", "paving-slabs"],
  },
  "seasonal": {
    title: "Seasonal",
    slug: "seasonal",
    metaTitle: "Best Christmas & Seasonal Garden Decor Reviews UK 2026",
    metaDescription: "Reviews of Christmas lights, outdoor decorations and seasonal garden products for UK homes.",
    description: "Reviews of seasonal garden products — Christmas lights, outdoor decorations and festive decor.",
    heroGradient: "bg-linear-to-br from-red-50 to-green-50 dark:from-red-950/20 dark:to-green-950/20",
    subcategories: ["christmas-decorations"],
  },
  "brands": {
    title: "Reviews By Brand",
    slug: "brands",
    metaTitle: "Garden Tool Reviews By Brand UK 2026 | All Major Brands",
    metaDescription: "Browse garden tool and machinery reviews by brand. Hyundai, Honda, Bosch, Makita, Stihl, Flymo, Mountfield and more - all independently reviewed.",
    description: "Browse our expert reviews organised by brand. Find reviews for all major garden tool and machinery manufacturers in one place.",
    heroGradient: "bg-linear-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20",
    subcategories: [],
  },
};
